import { CertificateExample, RealLifeUse } from "../pages/calculus/CalcBlocks";

/** Pass steps through unchanged (no English filler padding). */
export function lengthySteps(cores) {
  return (cores || []).map((c) => (typeof c === "string" ? { text: c } : c));
}

export function EightExamples({ items }) {
  return (
    <>
      {items.map((ex) => (
        <CertificateExample
          key={ex.number}
          number={ex.number}
          tier={ex.tier}
          title={ex.title}
          setup={ex.setup}
          steps={lengthySteps(ex.steps)}
          result={ex.result}
          check={ex.check}
          mistake={ex.mistake}
        />
      ))}
      {items[0]?.life ? <RealLifeUse>{items[0].life}</RealLifeUse> : null}
    </>
  );
}

export const DIFF_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Power rule",
    setup: "Compute $f'(x)$ if $f(x)=5x^4-3x+7$.",
    steps: [
      { text: "Write $f(x)=5x^4-3x+7$.", why: "Start from the given expression." },
      { text: "Use linearity: $f'(x)=5(x^4)'-3(x)'+(7)'$.", why: "$(af+bg)'=af'+bg'$." },
      { text: "Power rule: $(x^n)'=nx^{n-1}$, so $(x^4)'=4x^3$.", why: "Apply to each power." },
      { text: "Hence $5(x^4)'=5\\cdot 4x^3=20x^3$.", why: "Coefficient multiplies $n$." },
      { text: "$(x)'=1$, so $-3(x)'=-3$.", why: "Linear term." },
      { text: "$(7)'=0$.", why: "Constant rule." },
      { text: "Therefore $f'(x)=20x^3-3$.", why: "Combine terms." },
      { text: "Check at $x=1$: $f'(1)=17$. Difference quotient $\\dfrac{f(1+h)-f(1)}{h}\\to 17$ as $h\\to 0$.", why: "Numerical consistency." },
    ],
    result: "$f'(x)=20x^3-3$",
    check: "$f''(x)=60x^2$.",
    mistake: "Writing $5x^3$ instead of $20x^3$.",
    life: "For a cost model $C(q)=aq^4+\\cdots$, marginal cost is $C'(q)$.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Product rule",
    setup: "Differentiate $f(x)=x^2\\sin x$.",
    steps: [
      { text: "Set $u(x)=x^2$, $v(x)=\\sin x$, so $f=uv$.", why: "Identify the product." },
      { text: "$u'=2x$, $v'=\\cos x$.", why: "Differentiate each factor." },
      { text: "Product rule: $(uv)'=u'v+uv'$.", why: "Do not use $u'v'$ alone." },
      { text: "$f'(x)=2x\\sin x+x^2\\cos x$.", why: "Substitute." },
      { text: "Optional factor: $f'(x)=x\\bigl(2\\sin x+x\\cos x\\bigr)$.", why: "Useful for zeros." },
      { text: "At $x=0$: $f'(0)=0$.", why: "Special value." },
      { text: "Domain: $\\operatorname{dom}(f)=\\mathbb{R}$.", why: "Polynomial times sine." },
      { text: "Verify structure: two terms, one from each factor.", why: "Quick form check." },
    ],
    result: "$f'(x)=2x\\sin x+x^2\\cos x$",
    check: "At $x=0$, $f'=0$.",
    mistake: "Answering only $2x\\cos x$.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Quotient rule",
    setup: "Differentiate $f(x)=\\dfrac{x}{x^2+1}$.",
    steps: [
      { text: "Set $u=x$, $v=x^2+1$.", why: "Quotient $u/v$." },
      { text: "$u'=1$, $v'=2x$.", why: "Prepare derivatives." },
      { text: "$(u/v)'=\\dfrac{u'v-uv'}{v^2}$.", why: "Quotient rule." },
      { text: "Numerator: $1\\cdot(x^2+1)-x\\cdot 2x=x^2+1-2x^2=1-x^2$.", why: "Expand carefully." },
      { text: "Denominator: $(x^2+1)^2$.", why: "Keep squared." },
      { text: "So $f'(x)=\\dfrac{1-x^2}{(x^2+1)^2}$.", why: "Simplified form." },
      { text: "Critical points: $1-x^2=0\\Rightarrow x=\\pm 1$.", why: "Set numerator $0$ ($v\\neq 0$)." },
      { text: "As $x\\to\\infty$, $f'(x)\\sim -1/x^2\\to 0$.", why: "End behavior." },
    ],
    result: "$f'(x)=\\dfrac{1-x^2}{(x^2+1)^2}$",
    check: "$f'(0)=1>0$.",
    mistake: "Using $u'v+uv'$ in the numerator.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Chain rule",
    setup: "Differentiate $f(x)=\\cos(3x^2)$.",
    steps: [
      { text: "Outer $F(u)=\\cos u$, inner $u=3x^2$.", why: "Composition $F\\circ u$." },
      { text: "$F'(u)=-\\sin u$, $u'=6x$.", why: "Outer and inner derivatives." },
      { text: "Chain rule: $f'(x)=F'(u(x))\\cdot u'(x)$.", why: "Multiply." },
      { text: "$f'(x)=-\\sin(3x^2)\\cdot 6x=-6x\\sin(3x^2)$.", why: "Back-substitute." },
      { text: "Critical points include $x=0$ and $\\sin(3x^2)=0$.", why: "Set $f'=0$." },
      { text: "Compare with $(\\cos x)'=-\\sin x$: the factor $6x$ is $u'$.", why: "Conceptual check." },
      { text: "At $x=0$: $f'(0)=0$.", why: "Evaluate." },
      { text: "Domain: $\\mathbb{R}$.", why: "Entire functions." },
    ],
    result: "$f'(x)=-6x\\sin(3x^2)$",
    check: "$f'(0)=0$.",
    mistake: "Forgetting to multiply by $6x$.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Product and chain",
    setup: "Differentiate $f(x)=e^{2x}\\ln x$ for $x>0$.",
    steps: [
      { text: "Set $u=e^{2x}$, $v=\\ln x$.", why: "Product." },
      { text: "$u'=2e^{2x}$ (chain: $(e^{g})'=e^{g}g'$ with $g=2x$).", why: "Exponential chain." },
      { text: "$v'=1/x$.", why: "Logarithm rule." },
      { text: "$f'=u'v+uv'=2e^{2x}\\ln x+e^{2x}\\cdot\\dfrac{1}{x}$.", why: "Product rule." },
      { text: "Factor: $f'(x)=e^{2x}\\left(2\\ln x+\\dfrac{1}{x}\\right)$.", why: "Clean form." },
      { text: "Since $e^{2x}>0$, $\\operatorname{sign}(f')=\\operatorname{sign}\\bigl(2\\ln x+1/x\\bigr)$.", why: "Analysis." },
      { text: "At $x=1$: $f'(1)=e^{2}(0+1)=e^{2}$.", why: "Special value." },
      { text: "Domain: $x>0$.", why: "$\\ln x$ requires $x>0$." },
    ],
    result: "$f'(x)=e^{2x}\\bigl(2\\ln x+1/x\\bigr)$",
    check: "$f'(1)=e^2$.",
    mistake: "Dropping the factor $2$ in $(e^{2x})'$.",
  },
  {
    number: 6,
    tier: "Advanced",
    title: "Definition of the derivative",
    setup: "Use the limit definition to find $f'(2)$ for $f(x)=x^2$.",
    steps: [
      { text: "$f'(2)=\\displaystyle\\lim_{h\\to 0}\\dfrac{f(2+h)-f(2)}{h}$.", why: "Definition." },
      { text: "$f(2+h)=(2+h)^2=4+4h+h^2$, $f(2)=4$.", why: "Expand." },
      { text: "Numerator: $4+4h+h^2-4=4h+h^2$.", why: "Subtract." },
      { text: "$\\dfrac{4h+h^2}{h}=4+h$ for $h\\neq 0$.", why: "Cancel $h$." },
      { text: "$\\displaystyle\\lim_{h\\to 0}(4+h)=4$.", why: "Take the limit." },
      { text: "Power rule check: $(x^2)'=2x$, so at $x=2$ we get $4$.", why: "Agreement." },
      { text: "Tangent line at $x=2$: $y-4=4(x-2)$, i.e. $y=4x-4$.", why: "Geometry." },
      { text: "Never substitute $h=0$ before canceling.", why: "Avoid $0/0$ trap." },
    ],
    result: "$f'(2)=4$",
    check: "Matches $2x|_{x=2}$.",
    mistake: "Plugging $h=0$ into the unsimplified quotient.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Tangent line",
    setup: "Find the tangent line to $y=x^3$ at $x=1$.",
    steps: [
      { text: "Point of tangency: $y(1)=1^3=1$, so $(1,1)$.", why: "Need a point." },
      { text: "$y'=3x^2$, hence slope $m=y'(1)=3$.", why: "Derivative gives slope." },
      { text: "Point-slope form: $Y-1=3(X-1)$.", why: "Linearization." },
      { text: "Simplify: $Y=3X-2$.", why: "Slope-intercept." },
      { text: "Equivalently $P_1(x)=1+3(x-1)$ (degree-1 Taylor).", why: "Taylor link." },
      { text: "At $x=1.1$: $x^3=1.331$, line gives $3(1.1)-2=1.3$.", why: "Error $\\approx 0.031$." },
      { text: "Remainder is $O((x-1)^2)$.", why: "Order of error." },
      { text: "Instantaneous rate of $x^3$ at $1$ is $3$.", why: "Interpretation." },
    ],
    result: "$y=3x-2$",
    check: "Passes through $(1,1)$ with slope $3$.",
    mistake: "Using $y'=3x$ instead of $3x^2$.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Second derivative of a product",
    setup: "Find $f''(x)$ for $f(x)=x^4\\sin x$.",
    steps: [
      { text: "$f'=4x^3\\sin x+x^4\\cos x$ (product rule).", why: "First derivative." },
      { text: "Differentiate again as a sum of two products.", why: "Plan for $f''$." },
      { text: "$(4x^3\\sin x)'=12x^2\\sin x+4x^3\\cos x$.", why: "Product on first term." },
      { text: "$(x^4\\cos x)'=4x^3\\cos x+x^4(-\\sin x)$.", why: "Product on second; $(\\cos)'=-\\sin$." },
      { text: "Sum: $f''=12x^2\\sin x+8x^3\\cos x-x^4\\sin x$.", why: "Combine." },
      { text: "Group: $f''(x)=(12x^2-x^4)\\sin x+8x^3\\cos x$.", why: "Readable form." },
      { text: "At $x=0$: $f''(0)=0$.", why: "Evaluate." },
      { text: "Leibniz rule generalizes this pattern for higher $(uv)^{(n)}$.", why: "Bigger picture." },
    ],
    result: "$f''(x)=(12x^2-x^4)\\sin x+8x^3\\cos x$",
    check: "Each step used the product rule.",
    mistake: "Dropping the minus from $(\\cos x)'$.",
  },
];

export const DIFF_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Medium",
    title: "Related rates (circle)",
    setup: "If $A=\\pi r^2$ and $\\dfrac{dr}{dt}=2\\,\\mathrm{m/s}$, find $\\dfrac{dA}{dt}$ when $r=10\\,\\mathrm{m}$.",
    steps: [
      { text: "Given $A=\\pi r^2$ with $r=r(t)$.", why: "Linking equation." },
      { text: "Differentiate in $t$: $\\dfrac{dA}{dt}=2\\pi r\\dfrac{dr}{dt}$.", why: "Chain rule - before numbers." },
      { text: "Insert $r=10$, $\\dfrac{dr}{dt}=2$: $\\dfrac{dA}{dt}=2\\pi(10)(2)=40\\pi$.", why: "Instantaneous values last." },
      { text: "Units: $\\mathrm{m}^2/\\mathrm{s}$.", why: "Dimensional check." },
      { text: "So $\\dfrac{dA}{dt}=40\\pi\\,\\mathrm{m}^2/\\mathrm{s}$.", why: "State answer." },
      { text: "If $r$ is larger with same $\\dfrac{dr}{dt}$, then $\\dfrac{dA}{dt}$ is larger.", why: "Proportional to $r$." },
      { text: "Illegal: plug $r=10$ into $A$ first (makes $A$ constant).", why: "Common error." },
      { text: "Same pattern: tank $V=\\pi r^2 h$ problems.", why: "Transfer." },
    ],
    result: "$\\dfrac{dA}{dt}=40\\pi\\,\\mathrm{m}^2/\\mathrm{s}$",
    check: "Formula $2\\pi r\\,dr/dt$.",
    mistake: "Substituting $r$ before differentiating.",
    life: "Spill-area growth from radius radar is $dA/dt=2\\pi r\\,dr/dt$.",
  },
  {
    number: 2,
    tier: "Medium",
    title: "Absolute extrema",
    setup: "Find absolute max/min of $f(x)=x^3-3x$ on $[-2,2]$.",
    steps: [
      { text: "$f$ continuous on $[-2,2]$ $\\Rightarrow$ EVT applies.", why: "Existence." },
      { text: "$f'(x)=3x^2-3=3(x-1)(x+1)$.", why: "Critical points." },
      { text: "Critical $x=\\pm 1$ in $[-2,2]$; also endpoints $x=\\pm 2$.", why: "Candidate set." },
      { text: "$f(-2)=-2$, $f(-1)=2$, $f(1)=-2$, $f(2)=2$.", why: "Evaluate." },
      { text: "Absolute maximum value $2$ at $x=-1$ and $x=2$.", why: "Largest." },
      { text: "Absolute minimum value $-2$ at $x=-2$ and $x=1$.", why: "Smallest." },
      { text: "Sign of $f'$: $+/-$ about $-1$ (local max), $-/+$ about $1$ (local min).", why: "First-derivative test." },
      { text: "Report both locations for each extreme value.", why: "Completeness." },
    ],
    result: "Abs max $2$; abs min $-2$",
    check: "Four candidates compared.",
    mistake: "Ignoring endpoints.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Second derivative test",
    setup: "Classify critical points of $f(x)=x^4-2x^2$.",
    steps: [
      { text: "$f'(x)=4x^3-4x=4x(x-1)(x+1)$.", why: "Factor." },
      { text: "Critical points: $x=0,\\pm 1$.", why: "List." },
      { text: "$f''(x)=12x^2-4$.", why: "Differentiate again." },
      { text: "$f''(0)=-4<0$ $\\Rightarrow$ local maximum.", why: "Concave down." },
      { text: "$f''(\\pm 1)=8>0$ $\\Rightarrow$ local minima.", why: "Concave up." },
      { text: "$f(0)=0$, $f(\\pm 1)=-1$.", why: "Values." },
      { text: "$f$ even: $f(-x)=f(x)$, so symmetric extrema.", why: "Symmetry." },
      { text: "As $x\\to\\infty$, $f\\to\\infty$, so local max at $0$ is not global on $\\mathbb{R}$.", why: "Local vs absolute." },
    ],
    result: "Local max at $0$; local mins at $\\pm 1$",
    check: "Even function.",
    mistake: "Calling $x=0$ an absolute max on $\\mathbb{R}$.",
  },
  {
    number: 4,
    tier: "Advanced",
    title: "L'Hopital three times",
    setup: "Evaluate $\\displaystyle\\lim_{x\\to 0}\\dfrac{\\sin x-x}{x^3}$.",
    steps: [
      { text: "Direct sub: $\\dfrac{0-0}{0}=\\dfrac{0}{0}$ indeterminate.", why: "License L'Hopital." },
      { text: "$\\displaystyle\\lim_{x\\to 0}\\dfrac{\\cos x-1}{3x^2}$ still $\\dfrac{0}{0}$.", why: "Differentiate top and bottom." },
      { text: "$\\displaystyle\\lim_{x\\to 0}\\dfrac{-\\sin x}{6x}$ still $\\dfrac{0}{0}$.", why: "Again." },
      { text: "$\\displaystyle\\lim_{x\\to 0}\\dfrac{-\\cos x}{6}=-\\dfrac{1}{6}$.", why: "Now determinate." },
      { text: "Taylor: $\\sin x=x-\\dfrac{x^3}{6}+O(x^5)$ $\\Rightarrow$ numerator $-\\dfrac{x^3}{6}+O(x^5)$.", why: "Independent check." },
      { text: "Hence $\\dfrac{\\sin x-x}{x^3}\\to -\\dfrac{1}{6}$.", why: "Confirm." },
      { text: "Near $0$, $\\sin$, $\\cos$ are $C^\\infty$.", why: "Hypotheses OK." },
      { text: "Do not stop after one application while still $0/0$.", why: "Process." },
    ],
    result: "$-\\dfrac{1}{6}$",
    check: "Taylor agrees.",
    mistake: "Stopping after one L'Hopital step.",
  },
  {
    number: 5,
    tier: "Advanced",
    title: "Implicit differentiation",
    setup: "If $x^2+y^2=25$, find $\\dfrac{dy}{dx}$ at $(3,4)$.",
    steps: [
      { text: "Differentiate: $2x+2y\\dfrac{dy}{dx}=0$.", why: "Treat $y=y(x)$." },
      { text: "$\\dfrac{dy}{dx}=-\\dfrac{x}{y}$ for $y\\neq 0$.", why: "Solve for $y'$." },
      { text: "At $(3,4)$: $\\dfrac{dy}{dx}=-\\dfrac{3}{4}$.", why: "Evaluate." },
      { text: "Radius slope $\\dfrac{4}{3}$; product with $-\\dfrac{3}{4}$ is $-1$.", why: "Perpendicular check." },
      { text: "At $(5,0)$ the formula fails (vertical tangent).", why: "Singular points." },
      { text: "Same method for $F(x,y)=c$ level curves: $F_x+F_y y'=0$.", why: "Generalize." },
      { text: "Tangent vector can be taken as $\\langle y,-x\\rangle$ on the circle.", why: "Geometry." },
      { text: "Final: $y'=-3/4$ at the requested point.", why: "Box." },
    ],
    result: "$\\dfrac{dy}{dx}=-\\dfrac{3}{4}$ at $(3,4)$",
    check: "Tangent $\\perp$ radius.",
    mistake: "Forgetting to multiply by $y'$.",
  },
  {
    number: 6,
    tier: "Advanced",
    title: "Parametric derivatives",
    setup: "$x=t^2$, $y=t^3$. Find $\\dfrac{dy}{dx}$ and $\\dfrac{d^2y}{dx^2}$ at $t=1$.",
    steps: [
      { text: "$\\dfrac{dx}{dt}=2t$, $\\dfrac{dy}{dt}=3t^2$.", why: "Time rates." },
      { text: "$\\dfrac{dy}{dx}=\\dfrac{dy/dt}{dx/dt}=\\dfrac{3t}{2}$ for $t\\neq 0$.", why: "Parametric first derivative." },
      { text: "At $t=1$: $\\dfrac{dy}{dx}=\\dfrac{3}{2}$.", why: "Evaluate." },
      { text: "$\\dfrac{d}{dt}\\left(\\dfrac{dy}{dx}\\right)=\\dfrac{3}{2}$.", why: "Differentiate $3t/2$." },
      { text: "$\\dfrac{d^2y}{dx^2}=\\dfrac{\\frac{d}{dt}(dy/dx)}{dx/dt}=\\dfrac{3/2}{2t}=\\dfrac{3}{4t}$.", why: "Second parametric formula." },
      { text: "At $t=1$: $\\dfrac{d^2y}{dx^2}=\\dfrac{3}{4}$.", why: "Evaluate." },
      { text: "Eliminate $t$: $y=x^{3/2}$ ($x>0$) agrees.", why: "Independent check." },
      { text: "Do not use $\\dfrac{d^2y/dt^2}{d^2x/dt^2}$.", why: "Wrong formula." },
    ],
    result: "$\\dfrac{dy}{dx}=\\dfrac{3}{2}$, $\\dfrac{d^2y}{dx^2}=\\dfrac{3}{4}$",
    check: "Matches $y=x^{3/2}$.",
    mistake: "Wrong second-derivative formula.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "MVT inequality",
    setup: "Prove $|\\sin b-\\sin a|\\le |b-a|$.",
    steps: [
      { text: "Let $f(x)=\\sin x$ on $[a,b]$ (assume $a<b$).", why: "Set up." },
      { text: "$f\\in C[a,b]$ and $f$ differentiable on $(a,b)$.", why: "MVT hypotheses." },
      { text: "MVT: $\\exists c\\in(a,b)$ with $f'(c)=\\dfrac{\\sin b-\\sin a}{b-a}$.", why: "Apply MVT." },
      { text: "$f'(c)=\\cos c$ and $|\\cos c|\\le 1$.", why: "Bound." },
      { text: "Hence $\\left|\\dfrac{\\sin b-\\sin a}{b-a}\\right|\\le 1$.", why: "Take absolute values." },
      { text: "Multiply: $|\\sin b-\\sin a|\\le |b-a|$.", why: "Done." },
      { text: "So $\\sin$ is Lipschitz with constant $1$.", why: "Interpretation." },
      { text: "Near $0$, $|\\sin h|/|h|\\to 1$, so the constant is sharp.", why: "Sharpness." },
    ],
    result: "$|\\sin b-\\sin a|\\le |b-a|$",
    check: "Uses $|\\cos c|\\le 1$.",
    mistake: "Applying MVT without hypotheses.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Optimization",
    setup: "Rectangle of perimeter $40$ maximizes area. Find sides.",
    steps: [
      { text: "$2x+2y=40\\Rightarrow y=20-x$.", why: "Constraint." },
      { text: "Area $A(x)=x(20-x)=20x-x^2$, $0<x<20$.", why: "Single variable." },
      { text: "$A'(x)=20-2x=0\\Rightarrow x=10$.", why: "Critical point." },
      { text: "$y=10$ (a square).", why: "Back-substitute." },
      { text: "$A''(x)=-2<0$ $\\Rightarrow$ local max.", why: "Second test." },
      { text: "As $x\\to 0^+$ or $x\\to 20^-$, $A\\to 0$.", why: "Endpoints." },
      { text: "Hence absolute max on $(0,20)$ at $x=10$.", why: "Compare." },
      { text: "Max area $A=100$.", why: "Value." },
    ],
    result: "$10\\times 10$ square, $A=100$",
    check: "Isoperimetric: square wins.",
    mistake: "Maximizing perimeter instead of area.",
  },
];

export const INT_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Antiderivative (power reverse)",
    setup: "Find $\\displaystyle\\int(3x^2-4x+1)\\,dx$.",
    steps: [
      { text: "Linearity: $\\int(3x^2-4x+1)\\,dx=3\\int x^2\\,dx-4\\int x\\,dx+\\int 1\\,dx$.", why: "Split." },
      { text: "$\\displaystyle\\int x^n\\,dx=\\dfrac{x^{n+1}}{n+1}+C$ ($n\\neq -1$).", why: "Power reverse." },
      { text: "$3\\int x^2=3\\cdot\\dfrac{x^3}{3}=x^3$.", why: "First term." },
      { text: "$-4\\int x=-4\\cdot\\dfrac{x^2}{2}=-2x^2$.", why: "Second term." },
      { text: "$\\int 1\\,dx=x$.", why: "Third term." },
      { text: "Combine: $x^3-2x^2+x+C$.", why: "Add $+C$." },
      { text: "Differentiate: $(x^3-2x^2+x)'=3x^2-4x+1$.", why: "Verify." },
      { text: "Domain: $\\mathbb{R}$.", why: "Polynomial." },
    ],
    result: "$x^3-2x^2+x+C$",
    check: "Derivative recovers the integrand.",
    mistake: "Omitting $+C$.",
    life: "Rebuilding total cost from marginal cost $C'(q)$ uses an antiderivative.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Definite integral / FTC",
    setup: "Evaluate $\\displaystyle\\int_0^2 3x^2\\,dx$.",
    steps: [
      { text: "Antiderivative $F(x)=x^3$ since $F'=3x^2$.", why: "Find $F$." },
      { text: "FTC: $\\displaystyle\\int_a^b f=F(b)-F(a)$.", why: "Theorem." },
      { text: "$F(2)-F(0)=8-0=8$.", why: "Evaluate." },
      { text: "No $+C$ on definite integrals ($C$ cancels).", why: "Caution." },
      { text: "Alt: $3\\displaystyle\\int_0^2 x^2\\,dx=3\\left[\\dfrac{x^3}{3}\\right]_0^2=8$.", why: "Linearity." },
      { text: "Geometric meaning: area under $y=3x^2$ on $[0,2]$.", why: "Picture." },
      { text: "Integrand $\\ge 0$ on $[0,2]$ $\\Rightarrow$ value $\\ge 0$.", why: "Sign." },
      { text: "Final value $8$.", why: "Box." },
    ],
    result: "$8$",
    check: "Two methods agree.",
    mistake: "Leaving $+C$ on a definite integral.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Area under $y=\\sqrt{x}$",
    setup: "Compute the area under $y=\\sqrt{x}$ from $x=0$ to $x=4$.",
    steps: [
      { text: "Area $=\\displaystyle\\int_0^4 x^{1/2}\\,dx$.", why: "Set up." },
      { text: "$\\displaystyle\\int x^{1/2}\\,dx=\\dfrac{2}{3}x^{3/2}+C$.", why: "Power reverse." },
      { text: "$\\left[\\dfrac{2}{3}x^{3/2}\\right]_0^4=\\dfrac{2}{3}\\cdot 8-0=\\dfrac{16}{3}$.", why: "FTC." },
      { text: "$4^{3/2}=(4^{1/2})^3=2^3=8$.", why: "Arithmetic detail." },
      { text: "$f(x)=\\sqrt{x}$ continuous on $[0,4]$.", why: "FTC OK." },
      { text: "Compare to rectangle $4\\times 2=8$: $\\dfrac{16}{3}\\approx 5.33<8$.", why: "Sanity bound." },
      { text: "Endpoints $(0,0)$ and $(4,2)$.", why: "Graph check." },
      { text: "Area $=\\dfrac{16}{3}$.", why: "Box." },
    ],
    result: "$\\dfrac{16}{3}$",
    check: "Less than $8$.",
    mistake: "Using $\\frac12 x^{1/2}$ as antiderivative.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Signed area",
    setup: "Evaluate $\\displaystyle\\int_{-1}^{2} x\\,dx$.",
    steps: [
      { text: "$F(x)=\\dfrac{x^2}{2}$.", why: "Antiderivative." },
      { text: "$F(2)-F(-1)=2-\\dfrac{1}{2}=\\dfrac{3}{2}$.", why: "FTC." },
      { text: "$\\displaystyle\\int_{-1}^{0} x\\,dx=-\\dfrac{1}{2}$ (below axis).", why: "Split." },
      { text: "$\\displaystyle\\int_{0}^{2} x\\,dx=2$ (above axis).", why: "Split." },
      { text: "Net $=2+\\left(-\\dfrac{1}{2}\\right)=\\dfrac{3}{2}$.", why: "Agree." },
      { text: "Absolute area $=\\dfrac{1}{2}+2=\\dfrac{5}{2}\\neq$ net.", why: "Wording matters." },
      { text: "Odd integrand on symmetric interval about $0$ would net to $0$; here limits unequal.", why: "Symmetry note." },
      { text: "Answer $\\dfrac{3}{2}$.", why: "Box." },
    ],
    result: "$\\dfrac{3}{2}$",
    check: "Net of $-1/2$ and $2$.",
    mistake: "Reporting absolute area when net is asked.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Rewrite then integrate",
    setup: "Find $\\displaystyle\\int\\dfrac{x^2+1}{x}\\,dx$ for $x\\neq 0$.",
    steps: [
      { text: "$\\dfrac{x^2+1}{x}=x+\\dfrac{1}{x}$.", why: "Algebra first." },
      { text: "$\\displaystyle\\int\\left(x+\\dfrac{1}{x}\\right)dx=\\int x\\,dx+\\int\\dfrac{1}{x}\\,dx$.", why: "Split." },
      { text: "$\\displaystyle\\int x\\,dx=\\dfrac{x^2}{2}$.", why: "Power." },
      { text: "$\\displaystyle\\int\\dfrac{1}{x}\\,dx=\\ln|x|$.", why: "Log rule; use absolute value." },
      { text: "Answer $\\dfrac{x^2}{2}+\\ln|x|+C$.", why: "Combine." },
      { text: "Differentiate: $x+\\dfrac{1}{x}$.", why: "Verify." },
      { text: "Valid on $(-\\infty,0)$ and $(0,\\infty)$ (possibly different $C$).", why: "Domain pieces." },
      { text: "Do not treat as a blind quotient-rule reverse.", why: "Strategy." },
    ],
    result: "$\\dfrac{x^2}{2}+\\ln|x|+C$",
    check: "Derivative $x+1/x$.",
    mistake: "Writing $\\ln x$ without $|\\cdot|$.",
  },
  {
    number: 6,
    tier: "Advanced",
    title: "FTC with chain rule",
    setup: "Differentiate $g(x)=\\displaystyle\\int_1^{x^2}\\sin(t^2)\\,dt$.",
    steps: [
      { text: "Let $u(x)=x^2$, so $g(x)=\\displaystyle\\int_1^{u(x)}\\sin(t^2)\\,dt$.", why: "Prepare." },
      { text: "FTC: $\\dfrac{d}{du}\\displaystyle\\int_1^{u}\\sin(t^2)\\,dt=\\sin(u^2)$.", why: "Upper limit." },
      { text: "Chain: $g'(x)=\\sin(u(x)^2)\\cdot u'(x)$.", why: "Multiply by $u'$." },
      { text: "$u'=2x$ and $u^2=x^4$.", why: "Substitute." },
      { text: "Hence $g'(x)=2x\\sin(x^4)$.", why: "Final." },
      { text: "No need to antiderive $\\sin(t^2)$ explicitly.", why: "FTC power." },
      { text: "At $x=0$: $g'(0)=0$.", why: "Check." },
      { text: "Pattern: $\\dfrac{d}{dx}\\int_a^{u(x)}f(t)\\,dt=f(u(x))u'(x)$.", why: "General rule." },
    ],
    result: "$g'(x)=2x\\sin(x^4)$",
    check: "Chain factor $2x$.",
    mistake: "Answering only $\\sin(x^2)$.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Average value",
    setup: "Average value of $f(x)=x^2$ on $[0,3]$.",
    steps: [
      { text: "Average $=\\dfrac{1}{b-a}\\displaystyle\\int_a^b f(x)\\,dx$.", why: "Definition." },
      { text: "$\\dfrac{1}{3}\\displaystyle\\int_0^3 x^2\\,dx=\\dfrac{1}{3}\\left[\\dfrac{x^3}{3}\\right]_0^3=\\dfrac{1}{3}\\cdot 9=3$.", why: "Compute." },
      { text: "So average height is $3$.", why: "Interpret." },
      { text: "Midpoint $f(1.5)=2.25\\neq 3$ (not the same as $f$ of the mean).", why: "Caution." },
      { text: "Units same as $f$.", why: "Dimensions." },
      { text: "For $f\\ge 0$, equals height of rectangle with same area.", why: "Geometry." },
      { text: "Related later to RMS via $\\sqrt{\\text{average of }f^2}$.", why: "Forward link." },
      { text: "Answer $3$.", why: "Box." },
    ],
    result: "$3$",
    check: "Arithmetic checked.",
    mistake: "Using $f\\bigl(\\frac{a+b}{2}\\bigr)$ as the average value.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "IVP from velocity",
    setup: "If $v(t)=3t^2$ and $s(0)=1$, find $s(2)$.",
    steps: [
      { text: "$s'(t)=v(t)=3t^2$.", why: "Link position to velocity." },
      { text: "$s(t)=t^3+C$.", why: "Integrate." },
      { text: "$s(0)=1\\Rightarrow C=1$, so $s(t)=t^3+1$.", why: "Initial condition." },
      { text: "$s(2)=8+1=9$.", why: "Evaluate." },
      { text: "Net displacement $\\displaystyle\\int_0^2 v(t)\\,dt=[t^3]_0^2=8$.", why: "FTC check." },
      { text: "Then $s(2)=s(0)+8=9$.", why: "Agree." },
      { text: "If $v$ changes sign, net $\\neq$ distance traveled.", why: "Caution." },
      { text: "Motion model: $s(2)=9$.", why: "Box." },
    ],
    result: "$s(2)=9$",
    check: "Two methods agree.",
    mistake: "Forgetting the initial condition.",
  },
];

export const INT_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Medium",
    title: "Substitution",
    setup: "Evaluate $\\displaystyle\\int 2x\\cos(x^2)\\,dx$.",
    steps: [
      { text: "Inner $u=x^2$ appears with factor $2x\\,dx$.", why: "Pattern for $u$-sub." },
      { text: "Set $u=x^2$, then $du=2x\\,dx$.", why: "Define $u$." },
      { text: "$\\displaystyle\\int\\cos(x^2)\\cdot 2x\\,dx=\\int\\cos u\\,du$.", why: "Replace." },
      { text: "$\\int\\cos u\\,du=\\sin u+C$.", why: "Standard." },
      { text: "Back-sub: $\\sin(x^2)+C$.", why: "Return to $x$." },
      { text: "Differentiate: $\\cos(x^2)\\cdot 2x$.", why: "Verify." },
      { text: "If the $2x$ were missing, rewrite or choose another method.", why: "When sub fails." },
      { text: "Final $\\sin(x^2)+C$.", why: "Box." },
    ],
    result: "$\\sin(x^2)+C$",
    check: "Chain recovers integrand.",
    mistake: "Writing $\\sin(x^2)/(2x)$.",
    life: "Undoing a chain-rule sensor map $F(g(t))$ is substitution.",
  },
  {
    number: 2,
    tier: "Medium",
    title: "Integration by parts",
    setup: "Evaluate $\\displaystyle\\int x e^x\\,dx$.",
    steps: [
      { text: "LIATE: choose $u=x$, $dv=e^x\\,dx$.", why: "Heuristic." },
      { text: "$du=dx$, $v=e^x$.", why: "Compute." },
      { text: "$\\int u\\,dv=uv-\\int v\\,du=xe^x-\\int e^x\\,dx$.", why: "Parts formula." },
      { text: "$=xe^x-e^x+C=e^x(x-1)+C$.", why: "Simplify." },
      { text: "Differentiate: $e^x(x-1)+e^x=xe^x$.", why: "Verify." },
      { text: "Same idea for $\\int x^n e^x$ by repeating parts.", why: "Pattern." },
      { text: "Tabular method is optional speed-up.", why: "Technique note." },
      { text: "Final $e^x(x-1)+C$.", why: "Box." },
    ],
    result: "$e^x(x-1)+C$",
    check: "Product rule recovers $xe^x$.",
    mistake: "Choosing $u=e^x$ first.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Parts for $\\ln x$",
    setup: "Evaluate $\\displaystyle\\int\\ln x\\,dx$ ($x>0$).",
    steps: [
      { text: "Set $u=\\ln x$, $dv=dx$.", why: "Only workable split." },
      { text: "$du=\\dfrac{1}{x}\\,dx$, $v=x$.", why: "Prepare." },
      { text: "$uv-\\int v\\,du=x\\ln x-\\displaystyle\\int x\\cdot\\dfrac{1}{x}\\,dx$.", why: "Parts." },
      { text: "$=x\\ln x-\\int 1\\,dx=x\\ln x-x+C$.", why: "Simplify." },
      { text: "Differentiate: $\\ln x+x\\cdot\\dfrac{1}{x}-1=\\ln x$.", why: "Verify." },
      { text: "Domain $x>0$ (or use $\\ln|x|$ carefully on $\\mathbb{R}\\setminus\\{0\\}$).", why: "Domain." },
      { text: "Appears in entropy-type integrals $\\int p\\ln p$.", why: "Context." },
      { text: "Final $x\\ln x-x+C$.", why: "Box." },
    ],
    result: "$x\\ln x-x+C$",
    check: "Derivative is $\\ln x$.",
    mistake: "Answering $1/x$.",
  },
  {
    number: 4,
    tier: "Advanced",
    title: "Partial fractions",
    setup: "Evaluate $\\displaystyle\\int\\dfrac{1}{x^2-1}\\,dx$.",
    steps: [
      { text: "$x^2-1=(x-1)(x+1)$.", why: "Factor." },
      { text: "Write $\\dfrac{1}{(x-1)(x+1)}=\\dfrac{A}{x-1}+\\dfrac{B}{x+1}$.", why: "Ansatz." },
      { text: "$A(x+1)+B(x-1)=1$.", why: "Clear denominator." },
      { text: "$x=1\\Rightarrow 2A=1\\Rightarrow A=\\tfrac12$; $x=-1\\Rightarrow -2B=1\\Rightarrow B=-\\tfrac12$.", why: "Solve." },
      { text: "$\\displaystyle\\int\\left(\\dfrac{1/2}{x-1}-\\dfrac{1/2}{x+1}\\right)dx=\\dfrac12\\ln|x-1|-\\dfrac12\\ln|x+1|+C$.", why: "Integrate." },
      { text: "$=\\dfrac12\\ln\\left|\\dfrac{x-1}{x+1}\\right|+C$.", why: "Log laws." },
      { text: "Domain excludes $x=\\pm 1$.", why: "Poles." },
      { text: "Differentiate to verify the original integrand.", why: "Check." },
    ],
    result: "$\\dfrac12\\ln\\left|\\dfrac{x-1}{x+1}\\right|+C$",
    check: "Partial fractions recovered.",
    mistake: "Using $\\arctan$ (wrong factorization type).",
  },
  {
    number: 5,
    tier: "Advanced",
    title: "Improper integral to infinity",
    setup: "Does $\\displaystyle\\int_1^{\\infty} x^{-2}\\,dx$ converge?",
    steps: [
      { text: "Define as $\\displaystyle\\lim_{b\\to\\infty}\\int_1^{b} x^{-2}\\,dx$.", why: "Definition." },
      { text: "$\\displaystyle\\int x^{-2}\\,dx=-x^{-1}+C$.", why: "Antiderivative." },
      { text: "$\\left[-\\dfrac{1}{x}\\right]_1^{b}=-\\dfrac{1}{b}-\\left(-1\\right)=1-\\dfrac{1}{b}$.", why: "Evaluate." },
      { text: "$\\displaystyle\\lim_{b\\to\\infty}\\left(1-\\dfrac{1}{b}\\right)=1$.", why: "Limit finite." },
      { text: "Hence converges to $1$.", why: "Conclusion." },
      { text: "$p$-test: $\\int_1^\\infty x^{-p}$ converges for $p>1$; here $p=2$.", why: "Theory link." },
      { text: "Contrast: $p=1$ (harmonic) diverges.", why: "Boundary case." },
      { text: "Value $=1$.", why: "Box." },
    ],
    result: "Converges to $1$",
    check: "$p$-test agrees.",
    mistake: "Saying diverges only because the interval is infinite.",
  },
  {
    number: 6,
    tier: "Advanced",
    title: "Improper at an endpoint",
    setup: "Evaluate $\\displaystyle\\int_0^1 x^{-1/2}\\,dx$.",
    steps: [
      { text: "Singularity at $0$: write $\\displaystyle\\lim_{a\\to 0^+}\\int_a^1 x^{-1/2}\\,dx$.", why: "Definition." },
      { text: "$\\displaystyle\\int x^{-1/2}\\,dx=2x^{1/2}+C$.", why: "Power." },
      { text: "$\\left[2\\sqrt{x}\\right]_a^1=2-2\\sqrt{a}$.", why: "Evaluate." },
      { text: "$\\displaystyle\\lim_{a\\to 0^+}(2-2\\sqrt{a})=2$.", why: "Limit." },
      { text: "Converges to $2$.", why: "Conclusion." },
      { text: "Local $p$-test on $(0,1]$: $p=1/2<1$ converges.", why: "Theory." },
      { text: "Contrast $\\int_0^1 x^{-1}\\,dx$ diverges.", why: "Boundary." },
      { text: "Final $2$.", why: "Box." },
    ],
    result: "$2$",
    check: "Limit exists.",
    mistake: "Plugging $x=0$ into $2\\sqrt{x}$ without a limit.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Arcsin integral",
    setup: "Show $\\displaystyle\\int\\dfrac{1}{\\sqrt{a^2-x^2}}\\,dx=\\arcsin\\dfrac{x}{a}+C$ for $|x|<a$.",
    steps: [
      { text: "Recognize derivative pattern of $\\arcsin(x/a)$.", why: "Catalog." },
      { text: "Or set $x=a\\sin\\theta$, $dx=a\\cos\\theta\\,d\\theta$, $|\\theta|<\\pi/2$.", why: "Trig sub." },
      { text: "$\\sqrt{a^2-x^2}=\\sqrt{a^2(1-\\sin^2\\theta)}=a\\cos\\theta$ ($\\cos\\theta>0$).", why: "Simplify." },
      { text: "Integral becomes $\\displaystyle\\int\\dfrac{a\\cos\\theta}{a\\cos\\theta}\\,d\\theta=\\int d\\theta=\\theta+C$.", why: "Cancel." },
      { text: "$\\theta=\\arcsin(x/a)$.", why: "Back-sub." },
      { text: "Domain $|x|<a$ for real square root.", why: "Domain." },
      { text: "Differentiate $\\arcsin(x/a)$: $\\dfrac{1}{\\sqrt{1-(x/a)^2}}\\cdot\\dfrac{1}{a}=\\dfrac{1}{\\sqrt{a^2-x^2}}$.", why: "Verify." },
      { text: "Formula established.", why: "Box." },
    ],
    result: "$\\arcsin(x/a)+C$",
    check: "Matches derivative table.",
    mistake: "Using $\\arctan$ instead of $\\arcsin$.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Cyclic parts",
    setup: "Evaluate $\\displaystyle\\int e^x\\sin x\\,dx$.",
    steps: [
      { text: "Parts: $u=\\sin x$, $dv=e^x\\,dx$ $\\Rightarrow$ $du=\\cos x\\,dx$, $v=e^x$.", why: "First parts." },
      { text: "$I:=\\int e^x\\sin x=e^x\\sin x-\\int e^x\\cos x\\,dx$.", why: "Write $I$." },
      { text: "Parts on $\\int e^x\\cos x$: $u=\\cos x$, $dv=e^x\\,dx$.", why: "Second parts." },
      { text: "$\\int e^x\\cos x=e^x\\cos x+\\int e^x\\sin x\\,dx=e^x\\cos x+I$.", why: "Returns $I$." },
      { text: "So $I=e^x\\sin x-e^x\\cos x-I$.", why: "Substitute back." },
      { text: "$2I=e^x(\\sin x-\\cos x)\\Rightarrow I=\\dfrac{1}{2}e^x(\\sin x-\\cos x)+C$.", why: "Solve for $I$." },
      { text: "Differentiate to verify.", why: "Check." },
      { text: "Same pattern for $\\int e^{ax}\\sin(bx)\\,dx$.", why: "Generalize." },
    ],
    result: "$\\dfrac{1}{2}e^x(\\sin x-\\cos x)+C$",
    check: "Derivative recovers $e^x\\sin x$.",
    mistake: "Stopping before solving for $I$.",
  },
];
